import { Frame, Page } from "@shopify/polaris";
import { ReactifySearchProvider, Sensors } from "@usereactify/search";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { type PropsWithChildren } from "react";
import { useAppContext } from "~/providers/app";
import { Actions } from "./actions";
import { Empty } from "./empty";
import { Header } from "./header";
import { ModalConfig } from "./modal-config";
import { ModalIndices } from "./modal-indices";
import { Navigation, NavigationMobile } from "./navigation";

type LayoutProps = {
  title: string;
  fullWidth?: boolean;
};

export function Layout({ title, children, fullWidth = true }: PropsWithChildren<LayoutProps>) {
  const router = useRouter();
  const { validSession, index, shop, isCollection, urlHandle, pageTitle, viewUrl, storeUrl, market } = useAppContext();

  const [isNavOpen, setIsNavOpen] = React.useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = React.useState(false);

  const toggleNavigation = React.useCallback(() => setIsNavOpen((isNavOpen) => !isNavOpen), []);
  const toggleHelpMenu = React.useCallback(() => setIsHelpMenuOpen((isHelpMenuOpen) => !isHelpMenuOpen), []);
  const toggleUserMenu = React.useCallback(() => setIsUserMenuOpen((isUserMenuOpen) => !isUserMenuOpen), []);

  const handleNavigation = React.useCallback(
    (type: "search" | "redirect", url: string) => {
      if (type === "search") router.push(url);
      if (type === "redirect") window.open(`${storeUrl}${url}`, "_blank", "noopener,noreferrer");
    },
    [router, storeUrl]
  );

  const completeTitle = `${title}${pageTitle}`;

  return validSession ? (
    <Frame
      logo={{
        width: 32,
        url: "/",
        accessibilityLabel: "Reactify Search",
        topBarSource: "https://reactify.com.au/images/search-logo-flat.svg",
      }}
      topBar={
        shop.length > 0 && (
          <ReactifySearchProvider
            clientId="preview"
            mode="instant-search"
            index={index}
            shopifyPermanentDomain={shop}
            onRedirect={handleNavigation}
            market={market}
            includeFields={["*"]}
            configBaseUrl={process.env.NODE_ENV === "development" ? "http://localhost:5002/api/config" : undefined}
            devMode
          >
            <Sensors />
            <Header
              toggleNavigation={toggleNavigation}
              isHelpMenuOpen={isHelpMenuOpen}
              toggleHelpMenu={toggleHelpMenu}
              isUserMenuOpen={isUserMenuOpen}
              toggleUserMenu={toggleUserMenu}
            />
          </ReactifySearchProvider>
        )
      }
      navigation={<NavigationMobile />}
      showMobileNavigation={isNavOpen}
      onNavigationDismiss={toggleNavigation}
    >
      <Head>
        <title>{completeTitle} - Reactify Search Experience Preview</title>
      </Head>
      {!!shop.length && (
        <ReactifySearchProvider
          clientId="preview"
          mode={isCollection ? "collection" : "search"}
          index={index}
          shopifyPermanentDomain={shop}
          collectionHandle={urlHandle}
          market={market}
          includeFields={["*"]}
          configBaseUrl={process.env.NODE_ENV === "development" ? "http://localhost:5002/api/config" : undefined}
          devMode
        >
          <Sensors />
          <div className="Polaris-Frame__Navigation" id="AppFrameNav">
            <Navigation />
          </div>
          <Page
            title={completeTitle}
            primaryAction={
              viewUrl !== "" && {
                url: viewUrl,
                content: "View",
                external: true,
              }
            }
            fullWidth={fullWidth}
          >
            <Actions />
            {children}
          </Page>
          <ModalConfig />
          <ModalIndices />
        </ReactifySearchProvider>
      )}
    </Frame>
  ) : (
    <Empty />
  );
}

export default Layout;
